import * as React from 'react'

import { Box, Text, colors, dimensions, fonts } from '@factorialco/deprecated-design-system'
import type { AxisTickProps } from '@nivo/axes'
import { BarDatum, ResponsiveBar } from '@nivo/bar'
import type { CartesianMarkerProps } from '@nivo/core'

import i18n from 'modules/core/lib/i18n'

import { buildTheme } from './theme'

const HORIZONTAL_COLUMN_HEIGHT = 35
const VERTICAL_HEIGHT = 520

const HORIZONTAL_MARGIN = {
  top: 20,
  right: 20,
  bottom: 10,
  left: 180,
}

const VERTICAL_MARGIN = {
  top: 20,
  right: 20,
  bottom: 60,
  left: 20,
}

export interface Data extends BarDatum {
  value: string
  count: number
}

type CustomTickProps = AxisTickProps<string> & {
  direction: 'horizontal' | 'vertical'
}

const CustomTick = ({ x, y, value, direction }: CustomTickProps) => {
  const isHorizontal = direction === 'horizontal'
  const xTranslation = isHorizontal ? x - 10 : x
  const yTranslation = isHorizontal ? y : y + 20

  // This next value is the value but shortened to 18 chars, just if its total
  // length is greater than 20 chars. That because we don't have a looot of
  // space when the bars are horizontal and select labels are long
  const shortenedValue = value.length > 18 ? value.substr(0, 20) + '...' : value

  return (
    <g transform={`translate(${xTranslation}, ${yTranslation})`}>
      <text
        textAnchor={isHorizontal ? 'end' : 'middle'}
        dominantBaseline="middle"
        style={{
          textAlign: 'right',
          fill: colors.grey800,
          font: fonts.sizes['200'],
          fontWeight: fonts.weights.semibold,
        }}
      >
        {shortenedValue}
      </text>
    </g>
  )
}

const CustomTooltip = ({ data }: { data: Data }) => (
  <Box paddingX="s12" paddingY="s8" background="grey000White" boxShadow="s100">
    <Text.Normal>
      {i18n.tView('surveys.analytics.charts.tooltip', {
        value: <strong>{data.value || 0}</strong>,
        count: <span>{data.count}</span>,
      })}
    </Text.Normal>
  </Box>
)

interface Props {
  direction?: 'horizontal' | 'vertical'
  data: Data[]
  minValue?: number | 'auto'
  maxValue?: number | 'auto'
  onClick?: (label: string) => void
}

const Bars = ({ data, direction = 'horizontal', onClick }: Props) => {
  const isHorizontal = direction === 'horizontal'

  const height = isHorizontal ? 20 + data.length * HORIZONTAL_COLUMN_HEIGHT : VERTICAL_HEIGHT

  const markers: CartesianMarkerProps[] = [
    {
      axis: isHorizontal ? 'x' : 'y',
      value: 0,
      lineStyle: {
        stroke: colors.grey800,
        strokeWidth: 2,
      },
    },
  ]

  const renderTick = (props: AxisTickProps<string>) => (
    <CustomTick {...props} direction={direction} />
  )

  const axisLeft = isHorizontal ? { renderTick } : null
  const axisBottom = isHorizontal ? null : { renderTick }
  // NOTE: If the chart is horizontal, we want data to go from top to bottom, not from
  // bottom to top as the default
  const sortedData = isHorizontal ? data.reverse() : data

  return (
    <Box
      padding="s16"
      background="grey000White"
      // This is a hack so the chart has enough space:
      style={{
        height: `calc(${height + 'px'} + ${dimensions.s32}`,
      }}
    >
      <ResponsiveBar
        data={sortedData}
        keys={['count']}
        indexBy="value"
        layout={direction}
        margin={isHorizontal ? HORIZONTAL_MARGIN : VERTICAL_MARGIN}
        padding={0.25}
        valueScale={{ type: 'linear' }}
        colors={{ scheme: 'set3' }}
        colorBy="indexValue"
        theme={buildTheme()}
        tooltip={CustomTooltip}
        axisLeft={axisLeft}
        axisBottom={axisBottom}
        enableGridX={false}
        enableGridY={false}
        markers={markers}
        valueFormat={(value) => (value === 0 ? '' : String(value))}
        onMouseEnter={
          onClick
            ? (_datum, event) => ((event.target as SVGRectElement).style.cursor = 'pointer')
            : undefined
        }
        onMouseLeave={
          onClick
            ? (_datum, event) => ((event.target as SVGRectElement).style.cursor = 'normal')
            : undefined
        }
        onClick={onClick ? ({ data }) => onClick(data.value || '0') : undefined}
      />
    </Box>
  )
}

export default Bars
